<template>
  <div>
    <PurchaseFlowTabs
      :tabs="bankingInfoTabs"
      :active-index="currentSubstepIndex"
      @tab-click="handleTabClick"
      :is-review="is_review"
      :is-modal-edit="is_modal_edit"
    />
    <PF_Funding
      v-if="bankingInfoTabs[currentSubstepIndex]?.name === 'Funding your investment'"
      :plaid-link="plaidLink"
      :stripe-checkout="stripeCheckout"
      :is_review="is_review"
    />
    <PF_Distribution v-else :plaid-link="plaidLink" :is_review="is_review" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useDataStore, usePurchaseFlowStore, useUIStore } from "@/stores";
import { usePlaidLink } from "@/composables/usePlaidLink";
import { useStripeCheckout } from "@/composables/useStripeCheckout";
import PurchaseFlowTabs from "./PurchaseFlowUtilities/PurchaseFlowTabs.vue";
import PF_Funding from "./PurchaseFlowUtilities/PF_Funding.vue";
import PF_Distribution from "./PurchaseFlowUtilities/PF_Distribution.vue";

const plaidLink = usePlaidLink();
const stripeCheckout = useStripeCheckout();

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();
const UIStore = useUIStore();

const bankingInfoTabs = [{ name: "Funding your investment" }, { name: "Distribution" }];

const emit = defineEmits(["substep-change"]);
const props = defineProps({
  is_review: { type: Boolean, default: false },
  is_modal_edit: { type: Boolean, default: false },
});

const localSubstep = ref(1);

onMounted(async () => {
  // Refresh investments in case another tab signed one since login, so card
  // eligibility (no existing signed investment) reflects the latest state.
  await dataStore.setData(["investments"]);

  // If the user was redirected back here after 3DS authentication, pick up the result.
  try {
    const paymentIntent = await stripeCheckout.checkRedirectReturn();
    if (paymentIntent) {
      if (["succeeded", "requires_capture"].includes(paymentIntent.status)) {
        purchaseFlowStore.current_investment.funding_account = paymentIntent.id;
      } else {
        UIStore.animateNotificationAlert({
          type: "error",
          icon: "Info",
          message: `Card authentication did not complete (status: ${paymentIntent.status}). Please try again.`,
        });
      }
    }
  } catch (err) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: err.message || "Could not confirm card authentication. Please try again.",
    });
  }

  // If a card was previously authorized, verify the hold is still active.
  // If expired, wipe the pi_id so the card form renders instead of the summary.
  const inv = purchaseFlowStore.current_investment;
  const existingPiId = String(inv.funding_account ?? "").startsWith("pi_")
    ? inv.funding_account
    : null;
  if (inv.payment_method === "Card" && existingPiId) {
    const onHold = await stripeCheckout.checkHoldStatus(existingPiId);
    if (!onHold) {
      inv.funding_account = null;
    }
  }

  if (!purchaseFlowStore.current_investment.payment_method) {
    purchaseFlowStore.current_investment.payment_method = "ACH";
    purchaseFlowStore.current_investment.funding_account =
      dataStore.bank_accounts[0]?.record_id || null;
  }
  if (!purchaseFlowStore.current_investment.distribution_method) {
    purchaseFlowStore.current_investment.distribution_method = "ACH";
  }

  if (
    dataStore.bank_accounts.length === 1 &&
    purchaseFlowStore.current_investment.distribution_method === "ACH" &&
    !purchaseFlowStore.current_investment.distribution_account
  ) {
    purchaseFlowStore.current_investment.distribution_account =
      dataStore.bank_accounts[0].record_id;
  }

  plaidLink.initialize();
});

const currentSubstepIndex = computed(() => {
  if (props.is_review || props.is_modal_edit) return localSubstep.value - 1;
  return purchaseFlowStore.current_substep - 1;
});

function handleTabClick(index) {
  const targetSubstep = index + 1;
  const currentSubstep = localSubstep.value;

  if (props.is_review) {
    localSubstep.value = targetSubstep;
    return;
  }

  if (props.is_modal_edit) {
    if (targetSubstep <= currentSubstep) {
      localSubstep.value = targetSubstep;
      return;
    }

    const result = validateRange(currentSubstep - 1, index);
    if (!result.proceed) {
      localSubstep.value = result.failedAt + 1;
      return;
    }
    localSubstep.value = targetSubstep;
    return;
  }

  emit("substep-change", targetSubstep);
}

function validateFunding() {
  const investment = purchaseFlowStore.current_investment;

  if (!investment.payment_method) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: "Please select a payment method.",
    });
    return false;
  }

  if (investment.payment_method === "ACH" && !investment.funding_account) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: "Please connect a bank account for ACH funding.",
    });
    return false;
  }

  if (investment.payment_method === "Card") {
    const isAuthorized = String(investment.funding_account ?? "").startsWith("pi_");
    if (!isAuthorized && !stripeCheckout.stripe.value) {
      UIStore.animateNotificationAlert({
        type: "error",
        icon: "Info",
        message: "Please wait for the payment form to load.",
      });
      return false;
    }
  }

  return true;
}

function validateDistribution() {
  const investment = purchaseFlowStore.current_investment;

  if (
    !investment.distribution_method ||
    (investment.distribution_method === "Compounding Bonds" && !investment.is_compounded)
  ) {
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: "Please select a distribution method.",
    });
    return false;
  }

  if (investment.distribution_method === "ACH") {
    // If they have a connected bank account, we're good
    if (investment.distribution_account) {
      return true;
    }

    // If no connected bank, check if they uploaded a voided check
    if (purchaseFlowStore.voided_check) {
      return true;
    }

    // Neither bank account nor voided check
    UIStore.animateNotificationAlert({
      type: "error",
      icon: "Info",
      message: "Please connect a bank account or upload a voided check for ACH distributions.",
    });
    return false;
  }

  return true;
}

function validateSubstep(index) {
  const tab = bankingInfoTabs[index];

  if (tab?.name === "Funding your investment") {
    return {
      proceed: validateFunding(),
      error_messages: [],
      tabName: tab.name,
    };
  }

  if (tab?.name === "Distribution") {
    return {
      proceed: validateDistribution(),
      error_messages: [],
      tabName: tab.name,
    };
  }

  return {
    proceed: true,
    error_messages: [],
    tabName: tab?.name,
  };
}

function validateRange(fromIndex, toIndex) {
  for (let i = fromIndex; i < toIndex; i++) {
    const result = validateSubstep(i);
    if (!result.proceed) {
      return {
        proceed: false,
        failedAt: i,
        tabName: result.tabName,
        error_messages: result.error_messages,
      };
    }
  }
  return { proceed: true };
}

defineExpose({
  async validate() {
    const result = validateSubstep(currentSubstepIndex.value);
    return result;
  },
  validateRange,
  async beforeContinue(currentSubstep) {
    // Confirm the card authorization when leaving the Funding substep (substep 1).
    if (currentSubstep === 1 && purchaseFlowStore.current_investment.payment_method === "Card") {
      const inv = purchaseFlowStore.current_investment;
      const isAlreadyAuthorized = String(inv.funding_account ?? "").startsWith("pi_");

      if (!isAlreadyAuthorized) {
        try {
          const paymentIntent = await stripeCheckout.confirmPayment();
          purchaseFlowStore.current_investment.funding_account = paymentIntent.id;
        } catch (err) {
          UIStore.animateNotificationAlert({
            type: "error",
            icon: "Info",
            message: err.message || "Payment authorization failed. Please check your card details.",
          });
          return { proceed: false };
        }
      }
    }
    return { proceed: true };
  },
  afterContinue: async () => {
    if (
      purchaseFlowStore.current_investment.distribution_method === "ACH" &&
      purchaseFlowStore.voided_check
    ) {
      purchaseFlowStore.current_investment.distribution_account = null;
      purchaseFlowStore.current_investment.voided_check = true;
      await purchaseFlowStore.uploadVoidedCheck();
    }
    const promises = [
      purchaseFlowStore.saveOwnerProgress(),
      purchaseFlowStore.saveInvestmentProgress(),
    ];

    await Promise.all(promises);
  },
  substepCount: bankingInfoTabs.length,
});
</script>
